import React, { useCallback } from 'react';
import type { Coordinate2D, TempReferencePoints } from '../types';

/**
 * Props for the ReferenceLine component
 */
export interface ReferenceLineProps {
  /** Top reference point */
  topPoint: Coordinate2D;
  /** Bottom reference point */
  bottomPoint: Coordinate2D;
  /** Whether in update mode */
  isUpdateMode?: boolean;
  /** Temporary reference points during editing */
  tempPoints?: TempReferencePoints;
  /** Callback when point is updated */
  onPointUpdate?: (point: 'top' | 'bottom', x: number, y: number) => void;
  /** Optional class name */
  className?: string;
  /** Optional style */
  style?: React.CSSProperties;
  /** Stroke color when not in update mode */
  strokeColor?: string;
  /** Base stroke width in pixels (before scaling adjustment) */
  strokeWidth?: number;
  /** Current zoom scale to keep constant on-screen width */
  zoomScale?: number;
}

/**
 * Reference line component for PoleCanvas
 */
export const ReferenceLine: React.FC<ReferenceLineProps> = ({
  topPoint,
  bottomPoint,
  isUpdateMode = false,
  tempPoints = {},
  onPointUpdate,
  className = '',
  style,
  strokeColor = '#FFD700',
  strokeWidth = 4,
  zoomScale = 1,
}) => {
  // Use temp points if available, otherwise use props
  const effectiveTopPoint = tempPoints?.top || topPoint;
  const effectiveBottomPoint = tempPoints?.bottom || bottomPoint;
  
  // Return null if points are not defined
  if (!effectiveTopPoint || !effectiveBottomPoint) {
    return null;
  }

  // Handle point click for update mode
  const handlePointClick = useCallback((
    point: 'top' | 'bottom',
    e: React.MouseEvent
  ) => {
    if (isUpdateMode && onPointUpdate) {
      e.stopPropagation();
      const rect = (e.currentTarget as SVGElement).closest('svg')?.getBoundingClientRect();
      if (rect) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        onPointUpdate(point, x, y);
      }
    }
  }, [isUpdateMode, onPointUpdate]);

  return (
    <svg
      className={`pole-canvas-reference-line ${className}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: isUpdateMode ? 'auto' : 'none',
        ...style,
      }}
    >
      {/* Main reference line */}
      <line
        x1={effectiveTopPoint.x}
        y1={effectiveTopPoint.y}
        x2={effectiveBottomPoint.x}
        y2={effectiveBottomPoint.y}
        stroke={isUpdateMode ? '#ff6b6b' : strokeColor}
        strokeWidth={(isUpdateMode ? 3 : strokeWidth) / Math.max(zoomScale, 0.0001)}
        strokeDasharray={isUpdateMode ? '5,5' : 'none'}
      />
      
      {isUpdateMode && (
        <>
          {/* Top point marker (only in update mode) */}
          <circle
            cx={effectiveTopPoint.x}
            cy={effectiveTopPoint.y}
            r={8}
            fill={'#ff6b6b'}
            stroke="#fff"
            strokeWidth={2}
            style={{ cursor: 'pointer' }}
            onClick={(e) => handlePointClick('top', e)}
          />
          {/* Bottom point marker (only in update mode) */}
          <circle
            cx={effectiveBottomPoint.x}
            cy={effectiveBottomPoint.y}
            r={8}
            fill={'#ff6b6b'}
            stroke="#fff"
            strokeWidth={2}
            style={{ cursor: 'pointer' }}
            onClick={(e) => handlePointClick('bottom', e)}
          />
        </>
      )}
      
      {/* Labels for update mode */}
      {isUpdateMode && (
        <>
          <text
            x={effectiveTopPoint.x + 10}
            y={effectiveTopPoint.y - 10}
            fill="#333"
            fontSize="12"
            fontFamily="Arial, sans-serif"
          >
            Top
          </text>
          <text
            x={effectiveBottomPoint.x + 10}
            y={effectiveBottomPoint.y + 20}
            fill="#333"
            fontSize="12"
            fontFamily="Arial, sans-serif"
          >
            Bottom
          </text>
        </>
      )}
    </svg>
  );
};